import type { AppConfig, DeepPartial } from '@shared/app-config'
import type { AxiosInstance } from 'axios'
import type { TagAllowlistTracker } from '@/proxy/shared/tag-allowlist-tracker'
import { getApiRoutes } from '@shared/api/routes'
import { trackLoad, whenDefinedWithTimeout } from '@shared/api/web-components/wc-load-tracker'
import { hostLogger } from '@shared/logging/host-logger'

interface ComponentEntry {
  id: string
  name: string
  tag_name?: string
  entry_point?: string
  auto_register: boolean
  base_url?: string
  /**
   * JSON-Schema-shaped props metadata. Top-level `properties` keys are
   * the attribute names the component accepts on its custom element.
   */
  props?: { properties?: Record<string, unknown> }
}

interface ComponentsListResponse {
  success: boolean
  components: ComponentEntry[]
}

export interface AutoloadWidget {
  tagName: string
  /** Attribute names the WC accepts (from `props.properties` keys). */
  attributes: string[]
}

/**
 * Loads globally auto-registered web components and returns each one's
 * tag name plus the attribute names it accepts. The attribute list
 * feeds the chat-message sanitizer's `allowAdditionalTags` map so
 * markdown like `<example-mermaid definition="...">` keeps its props
 * through the sanitize pass instead of having them stripped to a bare
 * `<example-mermaid>` shell.
 *
 * Pass a `tracker` to also push every loaded tag + its attribute list
 * into the per-context tag-allowlist-tracker. When wired, `$W.sanitize`
 * picks up these tags automatically without a separate AppConfig
 * roundtrip — same source of truth across iframe and host contexts.
 */
export async function loadGlobalAutoloadWidgets(
  api: AxiosInstance,
  tracker?: TagAllowlistTracker,
): Promise<AutoloadWidget[]> {
  try {
    const { data } = await api.get<ComponentsListResponse>(getApiRoutes().components.list, {
      params: { auto_register: true },
    })
    if (!data.success || !data.components)
      return []

    const widgets: AutoloadWidget[] = []

    for (const component of data.components) {
      const tagName = component.tag_name
      if (!tagName || !component.entry_point || !component.base_url)
        continue

      const properties = component.props?.properties
      const attributes = properties && typeof properties === 'object'
        ? Object.keys(properties)
        : []
      widgets.push({ tagName, attributes })

      if (customElements.get(tagName))
        continue

      const entryUrl = new URL(`${component.base_url}/${component.entry_point}`, api.defaults.baseURL || window.location.origin)
      entryUrl.searchParams.set('declare-tag', tagName)

      // Register this script's load with the shared tag tracker so a concurrent
      // `$W.loadByTagName(tagName)` (or DOM-scan loader) finds the in-flight
      // promise and waits on it instead of re-fetching + re-defining (which
      // would throw on the duplicate `customElements.define`). The promise
      // resolves when `customElements.whenDefined` fires; it rejects if the
      // `<script>` itself fails to load, evicting the tag from the tracker so
      // retries can fire a fresh load.
      void trackLoad(tagName, () => new Promise<void>((resolve, reject) => {
        const script = document.createElement('script')
        script.setAttribute('type', 'module')
        script.setAttribute('src', entryUrl.toString())
        script.addEventListener('error', () => reject(new Error(`Autoload script failed: ${tagName} (${entryUrl})`)))
        // `whenDefinedWithTimeout` rejects after the default 30s if the
        // script loads but never calls `customElements.define` — without
        // the timeout this branch would silently hang forever on a buggy
        // WC that fetches OK but forgets to register.
        whenDefinedWithTimeout(tagName).then(resolve, reject)
        document.head.appendChild(script)
      })).catch((error) => {
        hostLogger.captureException(error, { context: 'global-autoload-script', tag: tagName })
      })
    }

    // Push every widget that survived the filter into the tracker so
    // `$W.sanitize` (and the host's chat-message sanitizer, via the
    // tracker → hostConfig.allowAdditionalTags bridge) can keep these
    // tags through sanitization. addMany is a single-fire batch so any
    // listener (e.g. the host's `updateAppConfig` bridge) sees one
    // coherent snapshot of the new state.
    if (tracker && widgets.length > 0)
      tracker.addMany(widgets)

    return widgets
  }
  catch (error) {
    hostLogger.captureException(error, { context: 'global-autoload-widgets' })
    return []
  }
}

/**
 * Loads auto-registered components and allows their tags + their
 * declared attributes in sanitized HTML. Host entries only — accepts
 * config accessors to avoid singleton dependency.
 *
 * Existing entries in `allowAdditionalTags` are preserved (consumers
 * may have added their own attribute lists). For tags new to the map
 * we install the full attribute list; for tags already present we
 * union in any attribute names not already there.
 */
export function registerAutoloadComponents(
  api: AxiosInstance,
  getAppConfig: () => AppConfig,
  updateAppConfig: (patch: DeepPartial<AppConfig>) => boolean,
  tracker?: TagAllowlistTracker,
) {
  loadGlobalAutoloadWidgets(api, tracker).then((widgets) => {
    if (!widgets.length)
      return
    // If a tracker is wired, it has already pushed every widget into
    // its allowlist (via `loadGlobalAutoloadWidgets`'s `addMany`) and
    // — if a subscriber is bridging it into AppConfig — the host's
    // sanitize wrappers will pick it up. Skip the direct AppConfig
    // write in that case to avoid a double-update.
    if (tracker)
      return
    const current = getAppConfig().hostConfig?.allowAdditionalTags ?? {}
    const newTags: Record<string, string[]> = { ...current }
    for (const { tagName, attributes } of widgets) {
      const existing = newTags[tagName]
      if (existing === undefined) {
        newTags[tagName] = [...attributes]
      }
      else {
        const merged = new Set(existing)
        for (const a of attributes) merged.add(a)
        newTags[tagName] = [...merged]
      }
    }
    updateAppConfig({ hostConfig: { allowAdditionalTags: newTags } })
  }).catch((error) => {
    hostLogger.captureException(error, { context: 'global-autoload-register' })
  })
}
